import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Phone, Mail, MessageCircle } from 'lucide-react';
import { motion, useInView } from 'framer-motion';

export const CTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-10 sm:py-14 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10" />
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/30 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/30 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1.5s' }} />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center space-y-8"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
            Connect with <span className="gradient-text-blue">VTV Enterprises</span> Today
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
            Ready to experience excellence in service? Get in touch with us through your preferred channel.
          </p>

          {/* Contact Methods */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8">
            <motion.a
              href="tel:+94704003030"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="glass-card p-6 rounded-xl hover-lift group text-center"
            >
              <div className="flex flex-col items-center gap-3">
                <div className="p-3 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                  <Phone className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold">Call Us</h3>
                <p className="text-sm text-muted-foreground">+94 70 400 30 30</p>
              </div>
            </motion.a>

            <motion.a
              href="https://wa.me/94704003030"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="glass-card p-6 rounded-xl hover-lift group text-center"
            >
              <div className="flex flex-col items-center gap-3">
                <div className="p-3 rounded-full bg-[#25D366]/10 group-hover:bg-[#25D366]/20 transition-colors">
                  <MessageCircle className="h-6 w-6 text-[#25D366]" />
                </div>
                <h3 className="font-semibold">WhatsApp</h3>
                <p className="text-sm text-muted-foreground">Chat Now</p>
              </div>
            </motion.a>

            <motion.a
              href="mailto:vtvlanka@gmail.com"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="glass-card p-6 rounded-xl hover-lift group text-center"
            >
              <div className="flex flex-col items-center gap-3">
                <div className="p-3 rounded-full bg-accent/10 group-hover:bg-accent/20 transition-colors">
                  <Mail className="h-6 w-6 text-accent" />
                </div>
                <h3 className="font-semibold">Email</h3>
                <p className="text-sm text-muted-foreground">vtvlanka@gmail.com</p>
              </div>
            </motion.a>
          </div>

          {/* Main CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="pt-8"
          >
            <Button asChild size="lg" className="bg-accent hover:bg-accent-dark glow-red text-lg px-8 py-6">
              <Link to="/contact">Get Started Now</Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
